import { Button, Input, Modal } from "@thetreep/ui";
import { useTranslation } from "react-i18next";
import { Field } from "./Field";
import { ReactElement, useEffect } from "react";
import {
  Controller,
  ControllerFieldState,
  FieldValues,
  Path,
  useForm,
} from "react-hook-form";
import Select from "./Select";
import { SelectToggle } from "./SelectToggle";
import { Icon } from "@thetreep/ui";
import { colors } from "@thetreep/ui";
interface CreationFormProps<T extends FieldValues> {
  isOpen: boolean;
  fields: Field<T, keyof T>[];
  title: string;
  onClose: () => void;
  onSubmit: (value: T) => void;
  isLoading?: boolean;
  error?: string;
}

export function CreationForm<T extends FieldValues>({
  title,
  fields,
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  error,
}: CreationFormProps<T>) {
  const { t } = useTranslation();
  const { handleSubmit, control, watch, reset } = useForm<T>({});
  const watchedValues = watch();

  // resets the form on close.
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  function parseNumber(s: string) {
    const v = parseInt(s, 10);
    return isNaN(v) ? s : v;
  }

  function getFieldElement<K extends keyof T>(
    field: Field<T, K>,
    value: T[K],
    fieldState: ControllerFieldState,
    onChange: (e: unknown) => void,
  ): ReactElement {
    switch (field.type) {
      case "text":
        return (
          <Input
            label={field.label}
            id={field.key as string}
            value={value}
            mandatory={!!field.creation?.rules?.required}
            onChange={onChange}
            error={fieldState.invalid}
            errorMsg={fieldState.error?.message}
            placeholder={field.placeholder}
            disabled={false}
          />
        );
      case "number":
        return (
          <Input
            type="number"
            label={field.label}
            id={field.key as string}
            value={value}
            mandatory={!!field.creation?.rules?.required}
            min={field.min}
            max={field.max}
            onChange={(e) => onChange(parseNumber(e.target.value))}
            error={fieldState.invalid}
            errorMsg={fieldState.error?.message}
            placeholder={field.placeholder}
            disabled={false}
          />
        );
      case "select":
        return (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-bold">
              {field.label}
              {field.creation?.rules?.required && "*"}
            </span>
            <Select
              options={field.options || []}
              value={value || ""}
              multiple={field.multi}
              onChange={onChange}
              search
              searchPlaceholder={field.searchPlaceholder}
              error={fieldState.invalid}
              errorMsg={fieldState.error?.message}
            />
          </div>
        );
      case "select-toggle":
        return (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-bold">{field.label}</span>
            <SelectToggle field={field} value={value} onChange={onChange} />
          </div>
        );
      default:
        return <></>;
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} content={{ title }} size="default">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full h-full flex flex-col justify-between gap-8">
          <div className="flex flex-col gap-4">
            {[...fields]
              .sort(
                (a, b) => (a.creation?.order || 0) - (b.creation?.order || 0),
              )
              .filter(
                (field) =>
                  field.key &&
                  (!field.condition || field.condition(watchedValues)),
              )
              .map((field) => {
                if (!field.key || !field.creation?.enabled) {
                  return null;
                }
                return (
                  <div key={field.key as string} className="flex flex-col">
                    <Controller
                      name={field.key as Path<T>}
                      control={control}
                      rules={field.creation?.rules || {}}
                      defaultValue={field.creation.defaultValue}
                      render={(el) =>
                        getFieldElement(
                          field,
                          el.field.value,
                          el.fieldState,
                          el.field.onChange,
                        )
                      }
                    />
                  </div>
                );
              })}
            {error && (
              <div className="flex flex-col gap-2">
                <div className="font-jakarta font-bold text-red-500 text-sm mt-2 flex items-center gap-2">
                  <Icon
                    src="close_circle_fill"
                    color={colors.errorStrong}
                    size={15}
                  />
                  {t("admin.errorFields")}
                </div>
                <div className="text-red-500 text-sm">{error}</div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-6">
            <Button
              type="reset"
              size="compact"
              kind="outline"
              onClick={onClose}
            >
              {t("cancel")}
            </Button>
            <Button type="submit" size="compact" isLoading={isLoading}>
              {t("confirm")}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
